import React, {useRef, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import PDComponent from '../../pd-component'
import usePdTagManager from '../../../../hooks/use-pd-tag-manager'

const SCROLLING_SPEED = 50 // px/sec, adjust as needed

export const ScrollingPromoBar = ({typeId, name, regions, backgroundColor}) => {
    const slides = regions?.[0]?.components
    const trackRef = useRef(null)
    const [animationCSS, setAnimationCSS] = useState('')
    const [duration, setDuration] = useState(0)
    const pdTagManager = usePdTagManager()
    const chidlCount = slides.length || 0
    const analyticsProps = {
        componentType: typeId,
        componentName: name ? `Scrolling - ${name}` : typeId
    }

    // Helper to recalculate animation on resize/content change
    const recalc = () => {
        if (trackRef.current) {
            const track = trackRef.current
            // Only measure the first set of slides
            const slidesCount = slides.length
            const singleSetWidth = Array.from(track.children)
                .slice(0, slidesCount)
                .reduce((acc, el) => acc + el.offsetWidth, 0)
            const newDuration = singleSetWidth / SCROLLING_SPEED

            setDuration(newDuration)

            setAnimationCSS(`
                @keyframes marquee {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-${singleSetWidth}px); }
                }
            `)
        }
    }

    // Recalculate on mount and whenever window resizes
    useEffect(() => {
        recalc()
        window.addEventListener('resize', recalc)
        return () => window.removeEventListener('resize', recalc)
    }, [])

    // Recalculate if slides change (dynamic content)
    useEffect(() => {
        recalc()
    }, [slides.join(',')])

    useEffect(() => {
        if (!chidlCount) return
        pdTagManager.viewComponent(typeId, analyticsProps.componentName, chidlCount)
    }, [])

    const allSlides = [...slides, ...slides, ...slides]

    return (
        <Box
            background={backgroundColor || 'black'}
            overflow="hidden"
            position="relative"
            _hover={{
                '.marquee-track': {
                    animationPlayState: 'paused'
                }
            }}
        >
            <style>{animationCSS}</style>
            <Box
                ref={trackRef}
                className="marquee-track"
                display="flex"
                width="fit-content"
                sx={{
                    willChange: 'transform',
                    animationTimingFunction: 'linear',
                    animationIterationCount: 'infinite',
                    animationName: duration ? 'marquee' : 'none',
                    animationDuration: duration ? `${duration}s` : undefined
                }}
            >
                {allSlides.map((component, idx) => (
                    <PDComponent
                        key={idx}
                        component={component}
                        analyticsProps={{
                            ...analyticsProps,
                            index: idx
                        }}
                    />
                ))}
            </Box>
        </Box>
    )
}

ScrollingPromoBar.propTypes = {
    typeId: PropTypes.string,
    name: PropTypes.string,
    backgroundColor: PropTypes.string,
    regions: PropTypes.array
}

export default ScrollingPromoBar
